import {
  type InvestorQuestion,
  QUESTION_BODY_MAX,
  QUESTION_STATUSES,
  questionMessageCreateSchema,
  type StaffQuestion,
} from '@fruuit/shared'
import { Avatar, Badge, Button, Callout, cn, StatusBadge, Textarea, useToast } from '@fruuit/ui'
import { Send } from 'lucide-react'
import { type FormEvent, useState } from 'react'
import { api } from '../../api/client'
import { useApiMutation } from '../../api/hooks'
import { dateTime } from '../engagements/format'
import { type FieldErrors, serverErrors, validate } from '../engagements/forms'
import { LANE_D_KEYS } from '../engagements/queries'

type Message = {
  id: string
  name: string
  role: 'investor' | 'manager'
  mine: boolean
  body: string
  createdAt: string
}

function investorMessages(q: InvestorQuestion): Message[] {
  return q.messages.map((m) => ({
    id: m.id,
    name: m.author_name,
    role: m.author_role,
    mine: m.mine,
    body: m.body,
    createdAt: m.created_at,
  }))
}

function staffMessages(q: StaffQuestion, userId: string | undefined): Message[] {
  return q.messages.map((m) => ({
    id: m.id,
    name: m.author.name,
    role: m.author_role,
    mine: m.author.id === userId,
    body: m.body,
    createdAt: m.created_at,
  }))
}

export type ThreadProps =
  | { variant: 'investor'; question: InvestorQuestion; showEngagement?: boolean }
  | {
      variant: 'staff'
      question: StaffQuestion
      currentUserId?: string
      showEngagement?: boolean
    }

/**
 * One question thread (P-ENG-001): the opening question, manager replies and investor
 * follow-ups, oldest first. Nothing is overwritten; each message keeps its author and time.
 */
export function QuestionThread(props: ThreadProps) {
  const q = props.question
  const messages =
    props.variant === 'investor'
      ? investorMessages(props.question)
      : staffMessages(props.question, props.currentUserId)
  const canPost =
    props.variant === 'investor' ? props.question.can_follow_up : props.question.can_reply
  const engagement =
    props.variant === 'investor' ? props.question.engagement : { ...props.question.engagement }
  const statusLabel = QUESTION_STATUSES.labels[q.status]

  return (
    <article className="rounded-lg border border-line bg-surface p-4 sm:p-5">
      <header className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="text-[15px] font-semibold leading-snug text-ink">{q.subject}</h3>
          <p className="mt-0.5 text-xs text-sage">
            {props.variant === 'investor'
              ? `${props.question.asked_by_me ? 'You asked' : `${props.question.asked_by_name} asked`}`
              : `${props.question.asked_by.name} · ${props.question.org.name}`}
            {props.showEngagement && engagement
              ? ` · ${engagement.issuer_name}: ${engagement.title}`
              : ''}
          </p>
        </div>
        <StatusBadge
          status={q.status === 'open' ? 'awaiting response' : q.status}
          label={statusLabel}
        />
      </header>
      {props.variant === 'investor' && !props.question.engagement ? (
        <Callout tone="warning" className="mt-3">
          This engagement is no longer available to your organisation. The thread stays in your
          records, but follow-ups are closed.
        </Callout>
      ) : null}
      <ol className="mt-4 space-y-3" aria-label="Messages">
        {messages.map((m) => (
          <li key={m.id} className={cn('flex gap-3', m.role === 'manager' && 'sm:pl-6')}>
            <Avatar name={m.name} size="sm" />
            <div
              className={cn(
                'min-w-0 flex-1 rounded-md px-3.5 py-2.5',
                m.role === 'manager' ? 'bg-teal-50' : 'bg-wash',
              )}
            >
              <p className="flex flex-wrap items-center gap-x-2 text-xs text-sage">
                <span className="font-semibold text-ink-soft">{m.mine ? 'You' : m.name}</span>
                {m.role === 'manager' ? <Badge tone="info">Engagement manager</Badge> : null}
                <span>{dateTime(m.createdAt)}</span>
              </p>
              <p className="mt-1 whitespace-pre-line text-sm leading-relaxed text-ink">{m.body}</p>
            </div>
          </li>
        ))}
      </ol>
      {q.status === 'open' && props.variant === 'investor' ? (
        <p className="mt-3 text-xs text-sage">The manager can respond in this workspace.</p>
      ) : null}
      {canPost ? (
        <Composer
          questionId={q.id}
          label={props.variant === 'investor' ? 'Add a follow-up' : 'Reply to the investor'}
          action={props.variant === 'investor' ? 'Send follow-up' : 'Send reply'}
        />
      ) : null}
    </article>
  )
}

function Composer({
  questionId,
  label,
  action,
}: {
  questionId: string
  label: string
  action: string
}) {
  const [body, setBody] = useState('')
  const [errors, setErrors] = useState<FieldErrors>({})
  const { toast } = useToast()
  const mutation = useApiMutation(
    (input: { body: string }, { requestId }) =>
      api.post(`/questions/${questionId}/messages`, input, { requestId }),
    {
      invalidate: LANE_D_KEYS,
      onSuccess: () => {
        setBody('')
        toast({ tone: 'success', message: 'Message sent. Nothing is emailed; it stays here.' })
      },
      onError: (error) => setErrors(serverErrors(error).fields),
    },
  )
  const formError = mutation.error ? serverErrors(mutation.error).message : null

  function submit(event: FormEvent) {
    event.preventDefault()
    const checked = validate(questionMessageCreateSchema, { body })
    if (!checked.ok) {
      setErrors(checked.errors)
      return
    }
    setErrors({})
    mutation.mutate(checked.data)
  }

  return (
    <form className="mt-4 border-t border-line-soft pt-4" onSubmit={submit} noValidate>
      <Textarea
        label={label}
        value={body}
        onChange={(event) => setBody(event.target.value)}
        maxLength={QUESTION_BODY_MAX}
        rows={3}
        error={errors.body}
      />
      {formError ? (
        <Callout tone="danger" role="alert" className="mt-3">
          {formError}
        </Callout>
      ) : null}
      <div className="mt-3 flex justify-end">
        <Button type="submit" size="sm" icon={<Send />} loading={mutation.isPending}>
          {action}
        </Button>
      </div>
    </form>
  )
}
